import { useEffect, useRef, useState } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const STANCE_BORDER = {
  supports: 'border-l-emerald-500',
  opposes: 'border-l-rose-500',
  neutral: 'border-l-gray-300 dark:border-l-gray-700',
};

const SOURCE_BADGE = {
  user: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-400',
  ai: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-400',
};

export default function PointItem({
  point,
  editable = false,
  reordering = false,
  onEdit,
  onDelete,
  onMove,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(point.text);
  const textareaRef = useRef(null);

  useEffect(() => {
    setDraft(point.text);
  }, [point.text]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editing]);

  const commit = () => {
    const text = draft.trim();
    setEditing(false);
    if (text && text !== point.text) onEdit?.(point.id, { text });
    else setDraft(point.text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(point.text);
      setEditing(false);
    }
  };

  return (
    <li
      className={`group border-l-2 pl-3 py-2 ${
        STANCE_BORDER[point.stance] || STANCE_BORDER.neutral
      } ${point.isNew ? 'animate-[fadeIn_0.3s_ease-out]' : ''}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {editing ? (
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={handleKeyDown}
              rows={2}
              className="w-full px-2 py-1 text-sm border border-indigo-300 dark:border-indigo-700 rounded-lg bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 resize-none"
            />
          ) : (
            <p
              onClick={editable ? () => setEditing(true) : undefined}
              className={`text-sm text-gray-800 dark:text-gray-200 leading-snug ${
                editable ? 'cursor-text' : ''
              }`}
            >
              {point.text}
            </p>
          )}
          <span
            className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium uppercase tracking-wide ${
              SOURCE_BADGE[point.source] || SOURCE_BADGE.ai
            }`}
          >
            {point.source === 'user' ? 'You' : 'AI'}
          </span>
        </div>

        {editable && reordering && (
          <div className="flex flex-col shrink-0">
            <button
              type="button"
              onClick={() => onMove?.(point.id, -1)}
              aria-label="Move point up"
              className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <ChevronUpIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onMove?.(point.id, 1)}
              aria-label="Move point down"
              className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <ChevronDownIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {editable && !reordering && !editing && (
          <button
            type="button"
            onClick={() => onDelete?.(point.id)}
            aria-label="Delete point"
            className="shrink-0 p-1 text-gray-300 dark:text-gray-600 hover:text-rose-500 dark:hover:text-rose-400 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
}
